import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { addMinutes, format, parseISO, isValid } from 'date-fns';
import fileService from '../../services/fileService';
import { useSnackbar } from 'notistack';

interface FileLockDialogProps {
  open: boolean;
  onClose: () => void;
  fileId: string;
  fileName: string;
  isLocked?: boolean;
  lockedBy?: string;
  lockedByName?: string;
  lockedAt?: string;
  lockedUntil?: string;
  onLockStatusChange?: (locked: boolean) => void;
}

const FileLockDialog: React.FC<FileLockDialogProps> = ({
  open,
  onClose,
  fileId,
  fileName,
  isLocked = false,
  lockedBy,
  lockedByName,
  lockedAt,
  lockedUntil,
  onLockStatusChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockComment, setLockComment] = useState('');
  const [expiresEnabled, setExpiresEnabled] = useState(false);
  const [expirationDateTime, setExpirationDateTime] = useState<Date | null>(
    addMinutes(new Date(), 60) // Default: 1 hour from now
  );
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setLockComment('');
      setExpiresEnabled(false);
      setExpirationDateTime(addMinutes(new Date(), 60));
    }
  }, [open]);

  const handleLockFile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await fileService.lockFile(fileId, {
        comment: lockComment,
        expiresAt: expiresEnabled && expirationDateTime ? expirationDateTime.toISOString() : undefined,
      });
      
      enqueueSnackbar(`${fileName} has been locked successfully`, { variant: 'success' });
      if (onLockStatusChange) onLockStatusChange(true);
      onClose();
    } catch (err) {
      setError('Failed to lock file. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockFile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await fileService.unlockFile(fileId);
      
      enqueueSnackbar(`${fileName} has been unlocked successfully`, { variant: 'success' });
      if (onLockStatusChange) onLockStatusChange(false);
      onClose();
    } catch (err) {
      setError('Failed to unlock file. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString?: string) => {
    if (!dateTimeString) return 'N/A';
    try {
      const parsedDate = parseISO(dateTimeString);
      if (!isValid(parsedDate)) return 'Invalid date';
      return format(parsedDate, 'PPpp'); // e.g., "Apr 29, 2023, 1:30 PM"
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            {isLocked ? (
              <LockIcon sx={{ mr: 1, color: 'warning.main' }} />
            ) : (
              <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
            )}
            <Typography variant="h6">
              {isLocked ? 'File Locked' : 'Lock File'}
            </Typography>
            <Tooltip title="About file locking">
              <IconButton 
                size="small" 
                onClick={() => setInfoDialogOpen(true)}
                sx={{ ml: 1 }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Typography variant="subtitle1" gutterBottom>
            {fileName}
          </Typography>
          
          {isLocked ? (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  This file is currently locked by {lockedByName || 'another user'}.
                </Typography>
              </Alert>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Locked by:
                  </Typography>
                  <Typography variant="body1">
                    {lockedByName || 'Unknown user'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Locked at:
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(lockedAt)}
                  </Typography>
                </Grid>
                {lockedUntil && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Lock expires at:
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(lockedUntil)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {lockedBy === 'currentUser' 
                  ? 'You have locked this file. You can unlock it to allow others to edit it.'
                  : 'This file is locked by another user. Only they or an administrator can unlock it.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                Locking this file will prevent others from editing it while you're working on it.
              </Typography>
              
              <TextField
                label="Lock Comment (optional)"
                fullWidth
                value={lockComment}
                onChange={(e) => setLockComment(e.target.value)}
                placeholder="E.g., Making major revisions"
                margin="normal"
                variant="outlined"
              />
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={expiresEnabled}
                    onChange={(e) => setExpiresEnabled(e.target.checked)}
                  />
                }
                label="Set expiration time"
                sx={{ mt: 1 }}
              />
              
              {expiresEnabled && (
                <FormControl fullWidth margin="normal">
                  <DateTimePicker
                    label="Lock Until"
                    value={expirationDateTime}
                    onChange={(newValue) => setExpirationDateTime(newValue)}
                  />
                </FormControl>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          
          {isLocked ? (
            lockedBy === 'currentUser' ? (
              <Button
                onClick={handleUnlockFile}
                color="warning"
                variant="contained"
                startIcon={<UnlockIcon />}
                disabled={loading}
              >
                {loading ? 'Unlocking...' : 'Unlock File'}
              </Button>
            ) : null
          ) : (
            <Button
              onClick={handleLockFile}
              color="primary"
              variant="contained"
              startIcon={<LockIcon />}
              disabled={loading}
            >
              {loading ? 'Locking...' : 'Lock File'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          About File Locking
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            File locking prevents other users from making changes to a file while you're working on it.
          </Typography>
          
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            When to use file locking:
          </Typography>
          <ul>
            <li>When you need to make extensive changes to a file</li>
            <li>When you want to prevent conflicting edits</li>
            <li>When working on critical documents</li>
          </ul>
          
          <Typography variant="body1" paragraph>
            Remember to unlock files when you're done working on them to allow others to make changes.
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Locks can be set to expire automatically, or you can manually unlock a file when you're finished.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileLockDialog;